<template>
    <div class="pb--5">
        <div class="row hidden-xs mb--10 header">  
            <div class="row__left">
                <div class="row__item pb--0">
                    <div class="text--bold text-gray-800">
                        INVOICE #{{ invoice?.invoiceNo }}
                    </div>
                </div>
            </div>

            <div class="row__right pr--0 pl--0 flex align-items-end">
                <BaseButton
                    label="Save as draft" 
                    class="mr--5"
                    variant="outline" 
                    variant-style="primary" 
                    :disabled="isSaveBtnDisabled || pageLoading"
                    @click="handleSubmitAction('save')"
                />

                <BaseButton
                    label="Send Invoice" 
                    :disabled="isSaveBtnDisabled || pageLoading"
                    class="mr--5"
                    @click="handleSubmitAction('send')"
                />

                <div class="relative flex align-items-center">
                    <div class="dropdown">
                        <BaseButton
                            id="invoiceDetailsDropDown"
                            prefix-icon 
                            data-bs-toggle="dropdown" 
                            aria-expanded="false"
                            class="mr--5"
                            icon-name="dots-horizontal-rounded"
                            icon- 
                            variant="secondary" 
                            variant-style="secondary" 
                        />

                        <List id="invoiceDetails">
                            <ListItem 
                                flex
                                pointer
                                data-bs-toggle="offcanvas"
                                data-bs-target="#offcanvasRight"
                                aria-controls="offcanvasRight"
                            >
                                <IconSvg name="show" width="16px" />
                                <span class="ml--10 text--sm text-gray-800">Preview invoice</span>
                            </ListItem>
                            <ListItem 
                                v-if="invoice.status !== 'draft'"
                                flex
                                pointer
                            >
                                <IconSvg name="duplicate" width="16px" />
                                <span class="ml--10 text--sm text-gray-800">Duplicate invoice</span>
                            </ListItem>
                            <ListItem 
                                v-if="invoice.status !== 'draft'"
                                flex
                                pointer
                            >
                                <IconSvg name="delete" fill="#e04060" width="16px" />
                                <span class="ml--10 text--sm text-error-400">Delete invoice</span>
                            </ListItem>
                        </List>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt--40 pb--35">
            <div class="invoice__details__row">
                <div class="invoice__details__row__left flex-3 pl--0">
                    <div class="flex col-12">
                        <div class="flex-1 mr--15 m-w-5">
                            <div>
                                <div class="col-12 flex align-items-center justify-content-between mb--5">
                                    <FormLabel title="Client information" class="mb--0" label-type="menu" info-type="required" />
                                    <BaseButton
                                        :label="showClientInfoView ? 'Switch view' : '+ New client'" 
                                        :show-spinner="false"
                                        bold
                                        :prefix-icon="showClientInfoView"
                                        icon-name="show"
                                        icon-fill="#289CA9"
                                        icon-width="16px"
                                        variant="ghost" 
                                        @submit="clientBtnAction" 
                                    />
                                </div>
    
                                <div class="col-12">
                                    <div class="invoice__row invoice__item">
                                        <div class="invoice__details--item m-w-100">
                                            <div class="flex align-items-center w-100">
                                                <div class="flex-2">
                                                    <template v-if="!showClientInfoView">
                                                        <CustomDropdown 
                                                            v-if="clientList.length"
                                                            id="clientDropdown" 
                                                            v-model="client" 
                                                            :options="clientList"
                                                            list-type="clients" 
                                                            index-label="name" 
                                                            :loading="searchClientsLoading"
                                                            :disabled="invoice.status !== 'draft'" 
                                                            @search="fetchClients" 
                                                            @select="v => setClient(v)" 
                                                        >
                                                            <template #prefix>
                                                                <div class="mr--10">
                                                                    <IconSvg name="user" fill="#64748B" />
                                                                </div>
                                                            </template>
                                                            <template #suffix="{ item }">
                                                                <div v-if="item.name === client?.name" @click="isShowClientInfoView = true">
                                                                    <IconSvg name="show" fill="#289CA9" width="12px" height="12px" />
                                                                </div>
                                                            </template>
                                                        </CustomDropdown>
                                                        
                                                        <p v-else class="text--sm text-gray-600">
                                                            No available clients.
                                                        </p>
                                                    </template>
                                                    <template v-else>
                                                        <div class="w-100 mr--35 mt--10 grid gap-2">
                                                            <div class="invoice__client__text">
                                                                Name: {{ client.name || 'N/A' }}
                                                            </div>
                                                            <div class="invoice__client__text">
                                                                Email address: {{ client.email || 'N/A' }}
                                                            </div>
                                                            <div class="invoice__client__text">
                                                                Phone number: {{ client.phoneNumber || 'N/A' }}
                                                            </div>
                                                            <div class="invoice__client__text text--capitalize">
                                                                Status: {{ client.status || 'N/A' }}
                                                            </div>
                                                            <div class="invoice__client__text text--capitalize">
                                                                Industry: {{ client.industry || 'N/A' }}
                                                            </div>                                                    
                                                        </div>
                                                    </template>
                                                </div>
                                            </div>
                                        </div>
        
                                        <div class="mt--30">
                                            <div class="flex justify-content-between align-items-center mb--5" style="min-width: 400px;">
                                                <FormLabel title="Billing emails" class="mb--0" label-type="menu" />
                                                <BaseButton 
                                                    v-if="!showCCEmails" 
                                                    variant="ghost" 
                                                    medium
                                                    label="+ Multiple CC emails" 
                                                    @submit="openCCEmails('add')"
                                                />
                                                <BaseButton 
                                                    v-else
                                                    variant="ghost" 
                                                    medium
                                                    label="- Manage email addresses" 
                                                    @submit="openCCEmails('manage')"
                                                />
                                            </div>
                                            <div class="email__wrap mt--15">
                                                <span v-if="!invoice.meta.ccEmails.length" class="text--sm text-gray-600">
                                                    No billing emails added.
                                                </span>
                                                
                                                <div v-else class="">
                                                    <p v-for="email in invoice.meta.ccEmails" :key="email" class="sub__tag text--sm text-gray-600 mb--5">
                                                        {{ email || '-' }}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="flex-1 m-w-5">
                            <div class="col-12 flex align-items-center justify-content-between mb--5">
                                <FormLabel title="Project information" class="mb--0" label-type="menu" info-type="required" />
                                <BaseButton
                                    :label="showProjectInfoView ? 'Switch view' : '+ New project'" 
                                    :show-spinner="false"
                                    bold
                                    :prefix-icon="showProjectInfoView"
                                    icon-name="show"
                                    icon-fill="#289CA9"
                                    icon-width="16px"
                                    variant="ghost" 
                                    @submit="projectBtnAction" 
                                />
                            </div>

                            <div class="col-12 flex align-items-center">
                                <div class="invoice__row invoice__item">
                                    <div class="invoice__details--item">
                                        <div class="flex align-items-center w-100">
                                            <div class="flex-2">
                                                <template v-if="!showProjectInfoView">
                                                    <CustomDropdown 
                                                        v-if="projectList.length"
                                                        id="projectDropdown" 
                                                        v-model="project" 
                                                        :options="projectList"
                                                        list-type="projects" 
                                                        index-label="title" 
                                                        :loading="fetchProjectsLoading"
                                                        :disabled="invoice.status !== 'draft'" 
                                                        @search="fetchProjects" 
                                                        @select="setProject" 
                                                    >
                                                        <template #prefix>
                                                            <div class="mr--5">
                                                                <IconSvg name="folder" fill="#848789" width="12px" height="12px" />
                                                            </div>
                                                        </template>
                                                        <template #suffix="{ item }">
                                                            <div v-if="item.title === project?.title" @click="isShowClientInfoView = true">
                                                                <IconSvg name="show" fill="#289CA9" width="12px" height="12px" />
                                                            </div>
                                                        </template>
                                                    </CustomDropdown>
                                                    <p v-else class="text--sm text-gray-600">
                                                        No available projects.
                                                    </p>
                                                </template>
                                                <template v-else>
                                                    <div class="w-100 mr--35 mt--10 grid gap-2">
                                                        <p class="invoice__client__text">
                                                            Title: <span class="text-gray-600 text--capitalize">{{ project.title || '-' }}</span>
                                                        </p>
                                                        <p class="invoice__client__text">
                                                            Status: <span class="text-gray-600 text--capitalize">{{ project.status || '-' }}</span>
                                                        </p>
                                                        <p class="invoice__client__text">
                                                            Deadline: <span class="text-gray-600">{{ $format_date(project.deadline) || '-' }}</span>
                                                        </p>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="invoice__row block">
                        <div class="invoice__calculate--wrap invoice__header ml--0 pr--0">
                            <div class="w-100">
                                <div class="flex justify-content-between mr--0">
                                    <FormLabel title="Items" class="mb--0" label-type="title" />
                                    <BaseButton 
                                        v-if="showAddItemBtn"
                                        label="+ Add new item" 
                                        variant="ghost" 
                                        class="p--0"
                                        @submit="addAnotherInvoiceItem" 
                                    />
                                </div>

                                <div class="mt--30 invoice__calculate--row form__row__right ml--0 w-100">
                                    <div class="form__row__left">
                                        <FormLabel title="Name" class="mb--0" label-type="menu" info-type="required" />
                                    </div>
                                    <div class="form__row__right invoice__calculate--row">
                                        <div class="flex-1 invoice__calculate--wrap" style="min-width: 120px;">
                                            <FormLabel title="Quantity" class="mb--0" label-type="menu" info-type="required" />
                                        </div>
                                        <div class="flex-2">
                                            <FormLabel title="Price" class="mb--0" label-type="menu" info-type="required" />
                                        </div>
                                        <div class="flex-2">
                                            <FormLabel title="Amount" class="mb--0" label-type="menu" info-type="required" />
                                        </div>
                                    </div>
                                </div>

                                <div class="mt--5">
                                    <div v-for="(item, i) in invoice.meta.items" :key="i">
                                        <div class="invoice__calculate--row w-100 relative ml--0" :class="{ 'mt--20' :i > 0 }">
                                            <div class="form__row__left">
                                                <input v-model="item.itemName" type="text" class="form-control form-control-sm">
                                            </div>
                    
                                            <div class="form__row__right invoice__calculate--row">
                                                <div class="invoice__calculate--wrap flex-1" style="min-width: 120px;">
                                                    <money
                                                        v-model="item.itemQuantity" 
                                                        v-bind="config"
                                                        class="form-control form-control-sm"
                                                        style="width: 80%" 
                                                        spellcheck="false"
                                                        @keydown.native="preventKeys"
                                                    />
                                                </div>

                                                <div class="flex-2" style="min-width: 120px;">
                                                    <money
                                                        v-model="item.itemPrice" 
                                                        v-bind="moneyConfig"
                                                        class="form-control form-control-sm"
                                                        style="width: 90%" 
                                                        spellcheck="false"
                                                        @keydown.native="preventKeys" 
                                                        @keyup.enter="addAnotherInvoiceItem"
                                                    />                                                                    
                                                </div>

                                                <div class="flex-2" style="min-width: 150px;">
                                                    <div class="text--upper text--xs">
                                                        {{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_amount(itemAmount(i)) }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-show="i > 0" class="invoice__calculate__close" @click="removeInvoiceItem(i)">
                                                <IconSvg name="x-circle" fill="#788490" />
                                            </div>
                                        </div>
            
                                        <span v-if="isInvoiceItemFilled(i)" class="form__errors flex align-items-center">
                                            <IconSvg name="alert" fill="#e04060" />
                                            <span class="ml--5 text-error-400 text--xs">
                                                Please fill the invoice item, quantity and unit price
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row invoice__section--item">
                        <div class="invoice__header">
                            <div class="grid gap-2">
                                <div class="invoice__compile--row">
                                    <div class="invoice__compile--label">
                                        Subtotal
                                    </div>
                                    <div class="invoice__compile--value ml--10 text--upper">
                                        {{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_amount(computedSubTotal) }}
                                    </div>
                                </div>

                                <div class="invoice__compile--row mt--0">
                                    <div class="flex align-items-center gap-1">
                                        <BaseButton 
                                            class="text--sm p--0" 
                                            :label="discountBtnLabel"
                                            variant="ghost" 
                                            medium
                                            is-content
                                            :disabled="!computedSubTotal"
                                            size="md"
                                            @submit="$emit('open-modal', 'showDiscountModal')" 
                                        />
                                        <BootstrapTooltip content="Discounts are applied before taxes." placement="right" custom-class="text--xs text--geist-mon">
                                            <IconSvg name="info" fill="#4b5563" />
                                        </BootstrapTooltip>
                                    </div>
                                    <div class="invoice__compile--value text--upper">
                                        -{{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_number(computedDiscount, 2) }}
                                    </div>
                                </div>

                                <div class="invoice__compile--row mt--5">
                                    <BaseButton 
                                        class="text--sm p--0" 
                                        label="+ Add tax"
                                        variant="ghost" 
                                        medium
                                        :disabled="!computedSubTotal"
                                        size="md"
                                        @submit="$emit('open-modal', 'showAddTaxModal')" 
                                    />
                                    <div class="invoice__compile--value text--upper">
                                        +{{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_number(computedTaxes, 2) }}
                                    </div>
                                </div>

                                <div class="invoice__compile--row mt--5">
                                    <div class="text--sm text--bold">
                                        Total
                                    </div>
                                    <div class="text--bold text--upper text--sm">
                                        {{ $country.getSymbol(invoice.currency, 'currency') }}{{ computedInvoiceTotal }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row invoice__section--item">
                        <div class="col-12 invoice__header flex align-items-center">
                            <FormLabel title="Memo" class="mb--0 mr--5" label-type="menu" />
                            <span class="text--xs text-label-info">(Max 300 words)</span>
                        </div>
                        <div class="col-6">
                            <div class="invoice__row invoice__item">
                                <div>
                                    <div class="invoice__details--item">
                                        <textarea
                                            id="exampleFormControlTextarea1" v-model="invoice.memo" name="memo" class="form-control form-control-sm"
                                            rows="3" maxlength="300"
                                            @change="handleFormFieldsChange"
                                        />
                                    </div>
                                    <p class="form-text text--xs mt--5">
                                        {{ invoice.memo.length }}/300 words used
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="invoice__details__row__right flex-1 pr--0">
                    <div class="row invoice__section--item mb--0">
                        <div class="col-12">
                            <FormLabel title="Payment information" class="mb--0" label-type="title" info-type="required" />
                        </div>

                        <div class="col-12 mt--20">
                            <div class="row invoice__section--item">
                                <div class="flex align-items-center mr--10 mb--10">
                                    <IconSvg name="money" width="1rem" fill="#64748B" />
                                    <div class="flex align-items-center">
                                        <FormLabel title="Currency" class="ml--5 mb--0 mr--5" label-type="menu" info-type="required" />
                                    </div>
                                </div>

                                <div class="col-12">
                                    <div class="invoice__details--item">
                                        <CustomDropdown 
                                            id="currencyDropdown" 
                                            v-model="invoice.currency" 
                                            size="sm"
                                            class="w-100 text--upper" 
                                            :options="currencies"
                                            list-type="currencies" 
                                            @search="searchCurrencies" 
                                            @select="setCurrency" 
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-12">
                            <div class="flex align-items-center justify-content-between invoice__header">
                                <div class="flex align-items-center mr--10">
                                    <IconSvg name="bank" width="1rem" fill="#64748B" />
                                    <FormLabel title="Payment method" class="mb--0 ml--5" label-type="menu" info-type="required" />
                                </div>
                                <BaseButton 
                                    class="p--0"
                                    label="Select" 
                                    variant="ghost" 
                                    bold
                                    icon-name="grid"
                                    prefix-icon
                                    :disabled="!invoice.currency"
                                    @submit="openBillingModal"
                                />
                            </div>
                            
                            
                            <template v-if="invoice.meta.billingMethod">
                                <FormLabel :title="invoiceDetails[invoice.meta.billingMethod]?.title" label-type="sm_mono" /> 

                                <div v-if="billingInfoData.accountDetails?.accountName" class="mt--20">
                                    <FormLabel title="Account details" class="text-gray-800 text--semi--bold" label-type="sm_mono" />

                                    <div class="grid gap-2">
                                        <p v-if="hasBillingInfoField('bankName')" class="invoice__client__text mt--5">
                                            Bank Name: {{ billingInfoData.accountDetails.bankName }}
                                        </p>
                                        <p v-if="hasBillingInfoField('accountName')" class="invoice__client__text">
                                            Account Name: {{ billingInfoData.accountDetails.accountName }}
                                        </p>
                                        <p v-if="hasBillingInfoField('accountNumber')" class="invoice__client__text">
                                            Account Number:  {{ billingInfoData.accountDetails.accountNumber }}
                                        </p>
                                        <p v-if="hasBillingInfoField('swiftCode')" class="invoice__client__text">
                                            SWIFT Code: {{ billingInfoData.accountDetails.swiftCode }}
                                        </p>
                                        <p v-if="hasBillingInfoField('routingNumber')" class="invoice__client__text">
                                            Routing number: {{ billingInfoData.accountDetails.routingNumber }}
                                        </p>
                                        <p v-if="hasBillingInfoField('iban')" class="invoice__client__text">
                                            IBAN: {{ billingInfoData.accountDetails.iban }}
                                        </p>
                                    </div>
                                </div>

                                <div v-else-if="billingInfoData.paymentLink" class="flex align-items-center mb--10 col-12 mt--10">
                                    <IconSvg name="link" />
                                    <a class="ml--5 project__item__title mt--0 text--sm text--underline" :href="billingInfoData.paymentLink.url">
                                        {{ billingInfoData.paymentLink.name }} payment link
                                    </a>
                                </div>

                                <div v-else>
                                    <p class="text--sm text-gray-600 text--capitalize">
                                        {{ invoice.meta.billingMethod?.name }}
                                    </p>
                                </div>
                            </template>
                        </div>
                    </div>

                    <div class="row invoice__section--item">
                        <div class="col-12 invoice__header">
                            <div class="flex align-items-center mr--10">
                                <IconSvg name="alarm" width="1rem" fill="#64748B" />
                                <FormLabel title="Due date" class="mb--0 ml--5" label-type="menu" info-type="required" />
                            </div>
                        </div>

                        <div class="col-12">
                            <div class="invoice__row invoice__item">
                                <div class="invoice__details--item">
                                    <div class="w-100">
                                        <CustomDropdown 
                                            id="dueDateDropdown" 
                                            v-model="deadlineType" 
                                            size="sm"
                                            index-label="name"
                                            :searchable="false"
                                            class="w-100" 
                                            :options="DATE_FILTERS"
                                            list-type="date filters" 
                                            @select="setDeadlineType" 
                                        />

                                        <div v-if="deadlineType.name === 'Custom'" class="mt--15">
                                            <FormLabel title="Select a date" html-for="nonBillableHours" />
                                            <v-date-picker
                                                v-model="invoice.dueDate"
                                                mode="dateTime" color="blue" 
                                                name="dueDate" 
                                                nav-visibility="click"
                                                @change="handleFormFieldsChange"
                                            >
                                                <template #default="{ inputValue, togglePopover }">
                                                    <input class="px-2 py-1 rounded text--sm form-control" :value="inputValue" @click="togglePopover">
                                                </template>
                                            </v-date-picker>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row invoice__section--item">
                        <div class="col-12 invoice__header mt--0">
                            <div class="flex align-items-center justify-content-between">
                                <div class="flex align-items-center mr--10">
                                    <IconSvg name="bell" width="1rem" fill="#64748B" />
                                    <div class="list--count">
                                        <FormLabel title="Reminders" label-type="menu" class="mb--0 ml--5">
                                            <span class="ml--3 zm__count__badge text--xs">{{ invoice.meta.reminders.length }}</span>
                                        </FormLabel>
                                    </div>
                                </div>

                                <BaseButton 
                                    variant="ghost" 
                                    label="+ Add reminders" 
                                    medium
                                    :disabled="!enableRemindersBtn"
                                    class="p--0" 
                                    @submit="openReminders"
                                />
                            </div>
                        </div>

                        <div v-if="invoice.meta.reminders && invoice.meta.reminders.length" class="col-12">
                            <span 
                                v-for="reminder in invoice.meta.reminders" :key="reminder" 
                                class="block project__item__desc text--xs mb--5"
                            >
                                {{ mapReminders[reminder] }}
                            </span>
                        </div>
                    </div>                                
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    IconSvg, 
    BaseButton,
    FormLabel, 
    CustomDropdown, 
    List,
    ListItem,
    BootstrapTooltip,
} from "@/components/ui/index";
import { assembleQueryList, convertStringAmountToInt, extractCCEmails, extractInvoiceDateFilters } from "@/utils/helpers";
import { billingMethods, taxTypes, mapReminders, DATE_FILTERS } from "@/utils/dummy";
import { paymentSupportedCurrencies } from "@/plugins/countries";
import { invoiceDetails } from "../dataComponents/auth";

export default {
    name: "InvoiceForm",

    components: {
        IconSvg, 
        BaseButton,
        FormLabel, 
        CustomDropdown, 
        List,
        ListItem,
        BootstrapTooltip,
    },

    props: {
        modelValue: { type: [String, Object, Array], default: undefined },

        defaultClient: {
            type: Object,
            default: undefined,
        },

        defaultProject: {
            type: Object,
            default: undefined,
        },

        invoiceType: {
            type: String,
            default: "new",
            validator: (value) => {
                return ["new", "existing"].includes(value);
            },
        },

        billingInfoData: {
            type: Object,
            default: undefined,
        },

        refreshPreviewData:  {
            type: Boolean,
            default: false,
        },

    },

    emits: [
        "open-modal",
        "submit-action",
        "save-complete",
        "send-complete",
        "update:modelValue",
        "initiate-preview",
    ],

    data() {
        return {
            DATE_FILTERS,
            taxTypes,
            mapReminders,
            billingMethods,
            invoiceDetails,
            invoice: this.modelValue || undefined,
            currencies: paymentSupportedCurrencies,
            config: {
                number: {
                    decimal: ".",
                    thousands: ",",          // The currency name or symbol followed by a space.
                    prefix: "",
                    suffix: "",
                    precision: 0,
                    masked: false,
                },
            },
            moneyConfig: {
                decimal: ".",
                thousands: ",",          // The currency name or symbol followed by a space.
                prefix: "",
                suffix: "",
                precision: 0,
                masked: false,
            },
            pageLoading: false,
            invoiceTaxType: { /** * @type{"percentage" | "flat"} */
                type: "",
                value: 0,
                name: "",
            },
            discountType: { /** * @type{"percentage" | "flat"} */
                type: "",
                value: 0,
                name: "",
            },
            isInvoiceItemEmpty: false,
            isShowClientInfoView: false,
            modalActionType: "add",
            isShowProjectInfoView: false,
            fetchProjectsLoading: false,
            searchClientsLoading: false,
            itemErrorsObj: {},
            clientList: [],
            projectList: [],
            invalidValues: {},
            innerTempEmails: [],
            upgradeField: "",
            providers: ["Paystack", "Flutterwave", "Stripe"],
            deadlineType: { id: "30_days", name: "30 days" },
            client: undefined,
            project: undefined,
            billingsMap: new Map(),
        };
    },

    computed: {
        enableRemindersBtn() {
            if (!this.deadlineType.id) return false;

            if (this.deadlineType?.id !== "Custom") return true;
            
            if (this.deadlineType.id === "Custom" && this.invoice.dueDate) return true;

            return false;
        },

        discountBtnLabel() {
            if (!this.invoice.meta?.discount || !this.invoice.meta?.discount?.type) return "";

            if (!this.computedDiscount) {
                if (this.invoice.meta.discount.type === "percentage") {
                    return "+ Add discount (0%)";
                }

                if (this.invoice.meta.discount.type === "flat") {
                    return "+ Add discount";
                }
            }

            if (this.invoice.meta?.discount.type === "percentage") {
                return `Edit discount (${this.invoice.meta.discount.value || 0 }%)`;
            }

            if (this.invoice.meta?.discount.type === "flat") {
                return "Edit discount";
            }

            return "";
        },

        user() {
            return this.$store.getters["auth/profile"];
        },

        showClientInfoView() {            
            if (this.client?.name && this.isShowClientInfoView) {
                return true;
            } else {
                return false;
            }
        },

        showProjectInfoView() {
            if (this.project?.title && this.isShowProjectInfoView) {
                return true;
            } else {
                return false;
            }
        },

        showAddItemBtn() {
            const firstItem = this.invoice.meta.items[0];

            if (firstItem.itemName && firstItem.itemPrice && firstItem.itemQuantity && firstItem.itemUnit) {
                return true;
            } else {
                return false;
            }
        },

        computedSubTotal() {
            if (this.invoice.meta.items.length > 0) {
                const sumItemsArr = this.invoice.meta.items
                    .map(item => item.itemPrice * item.itemQuantity)
                    .reduce((previousValue, currentValue) => previousValue + currentValue, 0);
                return sumItemsArr;
            } else {
                return 0;
            }
        },

        computedInvoiceTotal() {
            return this.$format_amount((this.computedTaxes + this.computedSubTotal) - this.computedDiscount, 2);
        },

        computedTaxes() {
            let final = 0;
            let taxCount = 0;
            let percentageTotal = 0;

            if (this.invoice?.meta.tax.length) {                
                for (let i = 0; i < this.invoice?.meta.tax.length; i++) {
                    const item = this.invoice?.meta.tax[i];

                    if (item.taxType === "percentage") {
                        percentageTotal = item.taxValue + percentageTotal;
                    } else {
                        taxCount = parseFloat(item.taxValue) + taxCount;
                    }
                }
            }

            if (percentageTotal > 0) {
                taxCount = taxCount + ((percentageTotal / 100) * this.computedSubTotal);
            }

            return taxCount;
        },

        computedDiscount() {
            if (this.invoice.meta?.discount.type === "percentage" && this.invoice.meta?.discount.value > 0) {
                return (this.invoice.meta?.discount.value / 100) * this.computedSubTotal;
            }

            return this.invoice.meta?.discount.value || 0;
        },

        clients() {
            return this.$store.getters["clients/clients"];
        },

        projects() {
            return this.$store.getters["project/projects"];
        },

        isSaveBtnDisabled() {
            if (!this.invoice.currency || !this.client?._id || !this.project?._id || !this.invoice.meta.items.length) {
                return true;
            } 

            if (!this.invoice.meta.billingMethod) {
                return true;
            }

            if (!this.enableRemindersBtn) { 
                return true;
            }

            if (!this.billingInfoData.paymentLink && !this.billingInfoData.accountDetails?.accountNumber && !this.billingInfoData.automatic) {
                return true;
            }
            
            return false;
        },

        previewInvoiceData() {
            return {
                invoice: {
                    invoiceNo: this.invoice.invoiceNo,
                    currency: this.invoice.currency,
                    createdAt: this.invoice.createdAt,
                    dueDate: this.invoice.dueDate,
                    tax: this.$format_amount(this.computedTaxes, 2),
                    discount: {
                        type: this.invoice.meta.discount.type,
                        value: this.$format_amount(this.computedDiscount, 2),
                        percentage: this.invoice.meta.discount.type ? this.invoice.meta.discount.value : 0,
                    },
                    subtotal: this.$format_amount(this.computedSubTotal, 2),
                    total: this.computedInvoiceTotal,
                    items: this.invoice.meta.items.filter(x => x.itemName || Number(x.itemPrice) || Number(x.itemQuantity) || x.itemUnit),
                },
                client: {
                    name: this.client?.name,
                    phoneNumber: this.client?.phoneNumber,
                    email: this.client?.email,
                    country: this.client?.country,
                    address: this.client?.name,
                },
                user: {
                    fullName: this.user?.fullName,
                    email: this.user?.email,
                    phoneNumber: this.user?.phoneNumber,
                },
            };
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        showCCEmails() {
            return this.invoice.meta.ccEmails.length;
        },

        selectedPaymentMethod() {
            return this.invoiceDetails[this.invoice.meta.billingMethod]?.title;
            // return this.billingsMap.get(this.invoice?.meta?.billingMethod || "");
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },
    },

    watch: {
        modelValue(newValue) {            
            this.updateInvoiceData(newValue);
        },

        "invoice.dueDate"(newVal) {
            if (newVal) {
                this.saveInvoiceDataOnLS();
            }
        },

        "invoice.deadlineType.id"(newVal) {
            if (newVal) {                
                this.saveInvoiceDataOnLS();
            }
        },

        "defaultClient": {
            handler(val) {
                if (val?._id) {
                    this.setClient(val);
                    this.isShowClientInfoView = true;
                }
            },
            deep: true, 
            immediate: true,
        },

        "defaultProject": {
            handler(val) {
                if (val?._id) {
                    this.setProject(val);
                    this.isShowProjectInfoView = true;

                    if (val?.currency) {
                        this.invoice.currency = val.currency;
                    }
                }
            },
            deep: true, 
            immediate: true,
        },

        "previewInvoiceData": {
            handler(newVal) {
                if (newVal) {
                    this.loadPreviewData();
                }
            },
            deep: true, 
            immediate: true,
        },
    },

    async mounted() {          
        if (this.clients.length) {
            this.clientList = this.clients;
        } else {
            await this.fetchClients();
        }

        if (this.projects.length) {
            this.projectList = this.projects;
        } else {
            await this.fetchProjects();
        }
    },

    methods: {
        convertStringAmountToInt,

        hasBillingInfoField(key) {
            if (this.billingInfoData.accountDetails && typeof this.billingInfoData.accountDetails === "object") {
                return this.billingInfoData.accountDetails?.hasOwnProperty(key);
            }
        },

        openReminders() {
            if (this.accountPlan === "free") {
                this.$emit("open-modal", "manageRemindersUpgrade");
            } else {
                this.$emit("open-modal", "showRemindersModal");
            }
        },

        loadPreviewData() {
            this.$emit("initiate-preview", this.previewInvoiceData);
        },

        getBillingMethodMap() {
            const billingMethods = this.billingMethods;

            for (let item of billingMethods) {
                if (item && item?.id) {
                    this.billingsMap.set(item.id, item);
                }
            }
        },

        updateInvoiceData(value) { // object
            if (!value) return;
            
            this.invoice = value;

            if (value?.deadlineType) {
                this.deadlineType = this.DATE_FILTERS.find(x => x.id === value?.deadlineType);
            }

            this.saveInvoiceDataOnLS();
        },

        setDeadlineType(value) {
            this.deadlineType = value;
            this.invoice.deadlineType = value;
            this.invoice.dueDate = new Date(extractInvoiceDateFilters(value?.id)).getTime();

            this.saveInvoiceDataOnLS();
        },

        async fetchClients(query) {
            try {
                this.searchClientsLoading = true;

                const pageQueryObj = {
                    page: 1,
                    type: "all",
                    name: query,
                };
    
                const buildResult = assembleQueryList(pageQueryObj);
                const result = await this.$store.dispatch("clients/fetchClients", { params: buildResult, external: true, refresh: true });
                
                this.clientList = result;
            } catch(error) {
                // console.log(error);
            } finally {
                this.searchClientsLoading = false;
            }
        },

        setClient(data = undefined) {     
            if (!data) return;

            this.client = data;

            if (data?.ccEmails) {                
                this.invoice.meta.ccEmails = extractCCEmails(data.ccEmails);
            } else {
                this.invoice.meta.ccEmails = [];
            }

            localStorage.setItem("z_invoice_client", JSON.stringify(data));

            this.isShowClientInfoView = true;
        },

        setProject(data = undefined) {     
            if (!data) return;

            this.project = data;

            if (data?.currency) {
                this.invoice.currency = data.currency;
            }
            
            localStorage.setItem("z_invoice_project", JSON.stringify(data));
            this.isShowProjectInfoView = true;
        },

        async fetchProjects(query) {
            try {
                this.fetchProjectsLoading = true;
    
                let pageQueryObj = {
                    page: 1,
                    type: "all",
                    title: query,
                };
    
                const buildResult = assembleQueryList(pageQueryObj);
                const result = await this.$store.dispatch("project/fetchProjects", { params: buildResult, refresh: true, external: true });
                
                this.projectList = result.data?.data;
            } catch (error) {
                // console.log(error);
            } finally {
                this.fetchProjectsLoading = false;
            }
        },

        clientBtnAction() {
            if (this.showClientInfoView) {
                this.isShowClientInfoView = !this.isShowClientInfoView;
            } else {
                this.$emit("open-modal", "createClientModal");
            }
        },

        projectBtnAction() {
            if (this.showProjectInfoView) {
                this.isShowProjectInfoView = !this.isShowProjectInfoView;
            } else {
                this.$emit("open-modal", "createProjectModal");
            }
        },

        computeTaxValue(tax) {
            if (tax.taxType === "percentage") {
                return `${tax.taxValue}%`;
            }
            return `${this.$country.getSymbol(this.invoice.currency, "currency")}${this.$format_amount(tax.taxValue)}`;
        },

        searchCurrencies(query) {            
            const initialList = this.currencies;

            if (query && typeof query === "string") {
                const filteredList = initialList.filter(c => c.toLowerCase().includes(query?.toLowerCase()));
                this.currencies = filteredList;
            } else {
                this.currencies = initialList;
            }
        },

        setCurrency(val) {            
            this.invoice.currency = val;
            this.saveInvoiceDataOnLS();
            this.currencies = paymentSupportedCurrencies;
        },

        openBillingModal() {
            this.$emit("open-modal", "showPaymentInfoModal");
        },

        openCCEmails(val) {
            this.$emit("open-modal", "showCCEmailsModal");

            if (this.invoice.meta.ccEmails.length && val === "manage") {
                this.innerTempEmails = this.invoice.meta.ccEmails;
            }
        },

        handleFormFieldsChange(event) {
            const value = event.target.value;
            const name = event.target.name;

            if (!name) return;
            
            this.invoice = {
                ...this.invoice,
                [name]: value,
            };

            this.saveInvoiceDataOnLS();
        },

        saveInvoiceDataOnLS() {            
            const data = {
                ...this.invoice,
                client: this.client,
                project: this.project,
            };

            // localStorage.removeItem("z_invoice_ongoing");
            localStorage.setItem("z_invoice_ongoing", JSON.stringify(data));
        },

        itemAmount(i) {            
            const item = this.invoice.meta.items[ i ];
            const quantity = item.itemQuantity;
            const unit = item.itemPrice;
            const price = parseFloat(quantity) * parseFloat(unit);
            
            this.invoice.meta.items[i].itemUnit = price;            
            return typeof price === "number" ? price.toFixed(3) : 0;
        },

        toggleProjectView() {
            this.isShowProjectInfoView = !this.isShowProjectInfoView;
        },

        addAnotherInvoiceItem() {            
            const isEmpty = this.checkIfInvoiceItemIsEmpty();

            if (isEmpty) {
                return;
            }  

            this.invoice.meta.items.push({ itemName: "", itemPrice: 0.00, itemQuantity: 0, itemUnit: 0.00 });
            this.saveInvoiceDataOnLS();
        },

        checkIfInvoiceItemIsEmpty() {
            let emptyCount = 0;
            const items = this.invoice.meta.items;

            // eslint-disable-next-line indent
            items.map((item, i) => {
                const emptyItemFields = (!item.itemName 
                || parseFloat(item.itemPrice) <= 0 
                || item.itemQuantity < 1 
                || item.itemUnit <= 0
                );

                if (emptyItemFields) {
                    emptyCount++;
                    this.itemErrorsObj[i] = true;
                } else {
                    delete this.itemErrorsObj[i];
                }
            });

            return emptyCount > 0;
        },

        isInvoiceItemFilled(index) {
            if (this.itemErrorsObj[index]) {
                return true;
            } else {
                return false;
            }
        },

        removeInvoiceItem(index = this.invoice.meta.items.length - 1) {
            /**
               * This will remove an invoice item from the list of items based on the index.
               * if none is defined, it removes the last item.
            */
            // this.$delete( this.invoice.meta.items, index );
            this.invoice.meta.items = this.invoice.meta.items.filter((elem, idx) => idx !== index);
        },

        getCurrentLSData() {
            const lsDataString = localStorage.getItem("z_invoice_ongoing");
            let lsDataObj;

            if (lsDataString && lsDataString !== "undefined") {
                lsDataObj = JSON.parse(lsDataString);    
            }

            return lsDataObj;
        },

        validateInvoiceItems() {
            delete this.invalidValues.client;
            delete this.invalidValues.project;
            delete this.invalidValues.billingMethod;
            delete this.invalidValues.computedInvoiceTotal;
            delete this.invalidValues.items;

            if (!this.client?._id) {
                this.invalidValues.client = true;
            }

            if (!this.project?.title || !this.project?._id) {
                this.invalidValues.project = true;
            } 

            if (!this.invoice.meta.billingMethod) {
                this.invalidValues.billingMethod = true;
            } 

            if (this.computedInvoiceTotal <= 0) {
                this.invalidValues.computedInvoiceTotal = true;
            }

            const checkIfItemsAreEmpty = this.checkIfInvoiceItemIsEmpty();
            
            if (checkIfItemsAreEmpty) {
                this.invalidValues.items = true;
            }

            if (this.invalidValues["accountDetails"]) {
                this.$toast.error("Fill in account details");
            }

            if (this.invalidValues["billingMethod"]) {
                this.$toast.error("Select a billing method");
            }

            if (this.invalidValues["project"]) {
                this.$toast.error("Select a project");
            }

            if (this.invalidValues["client"]) {
                this.$toast.error("Select or create a new client");
            }

            if (this.invalidValues["items"]) {
                this.$toast.error("Fill in invoice items to continue");
            }

            const isInvalid = !!Object.keys(this.invalidValues).length > 0;
            return isInvalid;
        },

        createInvoicePayload() {
            const payload = {
                invoiceNo: this.invoice.invoiceNo,
                currency: this.invoice.currency.toLowerCase(),
                dueDate:this.invoice.dueDate,
                deadlineType: this.deadlineType?.id,
                amountPaid: this.invoice.amountPaid,
                memo: this.invoice.memo,
                meta: {
                    ...this.invoice.meta,
                    ccEmails: this.invoice.meta.ccEmails?.join(","),
                    billingMethod: this.invoice.meta.billingMethod?.id || this.invoice.meta.billingMethod || "",
                    discount: {
                        value: Number(this.invoice.meta.discount.value),
                        type: this.invoice.meta.discount.type,
                    },
                },
                project: {
                    _id: this.project?._id,
                    title: this.project?.title,
                },
                client: {
                    _id: this.client._id,
                    name: this.client.name,
                    email: this.client.email,
                    phoneNumber: this.client.phoneNumber,
                    address: this.client.address,
                    country: this.client.country,
                },
                status: this.invoice.status,
                totalAmount: this.convertStringAmountToInt(this.computedInvoiceTotal || ""),
                accountDetailsId: this.invoice.accountDetailsId,
                paymentLinkId: this.invoice.paymentLinkId || this.billingInfoData.paymentLink?._id,
            };

            return payload;
        },

        async handleSaveInvoice() {
            try {
                const payload = this.createInvoicePayload();
                payload.status = "draft";

                // did this because we are not sending any email to the client, only updating it in the db
                delete payload.project;
                delete payload.client;
                
                payload.projectId = this.project?._id; 
                payload.clientId = this.client?._id;

                await this.$store.dispatch("invoice/saveInvoiceDraft", { payload, client: this.client, type: this.invoiceType });
                this.$emit("save-complete");
            } catch (error) {                
                throw error;
            }
        },

        async handleSendInvoice() {
            try {
                const payload = this.createInvoicePayload();
                
                if (this.invoiceType === "new") {
                    payload.status = "issued";
                } else {
                    payload._id = this.invoice._id;
                }

                await this.$store.dispatch("invoice/sendInvoice", { payload, type: this.invoiceType, client: this.client });
                this.$emit("send-complete");
            } catch (error) {
                throw error;
            }
        },

        async handleSubmitAction(val) {
            try {
                const isInvalid = this.validateInvoiceItems();
                
                if (isInvalid) {
                    return;
                };

                this.pageLoading = true;

                if (val === "send") {
                    await this.handleSendInvoice();
                } else if (val === "save") {
                    await this.handleSaveInvoice();
                } 
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.pageLoading = false;
            }
        },

        async deleteInvoice() {
            try {
                this.pageLoading = true;

                const id = this.$route.params.id;

                await this.$store.dispatch("auth/deleteInvoice", id);

                this.$toast.success("Invoice deleted successfully");
                this.$router.push({ path: "/dashboard/invoices" });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.pageLoading = false;
            }
        },

        /**
         * Returns the sum of all the elements fields in an array of elements
        */
        sumElements( elems, field ) {
            if (elems.length === 0) return 0;

            let sum = parseFloat(elems[ 0 ][ field ]);
            
            if (elems.length === 1 ) return sum;
            
            else {
                for( let i = 1; i < elems.length; i++ ) {
                    sum += parseFloat( elems[ i ][ field ] );
                }
                return sum;
            }
        },

        preventKeys(event) {
            if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105) || event.keyCode == 8 || event.keyCode == 9 || event.keyCode == 190 || event.keyCode == 37 || event.keyCode == 39) {
            } else {
                return event.preventDefault();
            }
        },

        setReminders(event, item) {
            const checked = event.target.checked;

            if (checked) {
                this.invoice.meta.reminders.push({
                    remindersName: item.id,
                    reminderDate: new Date(),
                });
            } else {
                this.invoice.meta.reminders = this.invoice.meta.reminders.filter(x => {
                    return x.remindersName !== item.id;
                });
            } 

            this.saveInvoiceDataOnLS();
        },

        removeTaxItem(index = this.invoice.meta.tax.length - 1) {
            this.invoice.meta.tax = this.invoice.meta.tax.filter((elem, idx) => idx !== index);
        },
    },
};
</script>

<style lang="scss" scoped>
.row  {
  &.header {
    justify-content: space-between;
  }

  .row__left, .row__right {
    width: fit-content;
    max-width: fit-content;
  }
}

</style>